// ============================================================
// Die Trefferliste unter dem Suchfeld der Kopfleiste.
//
// Bis hierher tat das Feld genau eines: es schrieb in `filter.suche` und die
// Karte wurde leerer. Wer einen Namen halb tippte, sah nicht, ob es ihn
// überhaupt gibt — nur, dass immer weniger übrig blieb. Diese Liste sagt
// während des Tippens, WAS gefunden wurde, und bringt einen mit einem Griff
// hin (dieselbe Geste wie im webgis, `screens/karte/MapSearch.tsx`).
//
// Fünf Entscheidungen, die man beim Lesen sonst für Zufall hielte:
//
// 1. DREI VON VIER ABSCHNITTEN BRAUCHEN KEIN NETZ. Projekte, Menschen und
//    Büros stehen längst im Zwischenspeicher (`useStand`, eine Abfrage beim
//    Anmelden). Für sie eine Route zu bauen hieße, für Daten im Speicher über
//    das Netz zu gehen — und zwar bei jedem Tastendruck. Nur die ORTE kommen
//    von draußen (Photon), und die sind entprellt.
//
// 2. ENTPRELLT WIRD TROTZDEM ALLES, mit 220 ms wie drüben. Nicht wegen der
//    Rechenzeit — 70 Projekte filtern kostet nichts —, sondern wegen der
//    Ruhe: eine Liste, die bei jedem Buchstaben umspringt, ist schwerer zu
//    lesen als eine, die kurz wartet.
//
// 3. DER FILTER BLEIBT. Tippen filtert die Karte weiter wie bisher; die Liste
//    kommt DAZU. Wer sie wegklickt, hat immer noch seinen Filter — das ist
//    die Arbeitsweise, die im Haus schon eingeübt ist. Und weil beide
//    dasselbe Wort sehen, prüfen beide es auch am SELBEN Text:
//    `Stift.suchtext` (Name, Nummer, Kürzel, Ort, Phase, Art, Team, Büros,
//    nächste Abgabe). Sonst dünnte die Karte auf „Wettbewerb" aus, während
//    die Liste darüber „keine Treffer" meldete.
//
// 4. EINE FLACHE TASTATURFOLGE über alle Abschnitte. ↑ und ↓ laufen durch die
//    ganze Liste, nicht je Abschnitt; die Überschriften werden übersprungen.
//    Wer tippt und zweimal ↓ drückt, will den zweiten Treffer, egal in
//    welchem Kasten er steht. Enter nimmt die markierte Zeile — am RECHNER.
//    Am Telefon heißt Enter „fertig getippt" (die Kopfleiste nimmt dort nur
//    die Tastatur weg, die Liste bleibt für den Daumen stehen); dieselbe
//    Taste darf nicht auf zwei Bildschirmen zwei Dinge tun.
//
// 5. GEWÄHLT WIRD MIT DERSELBEN GESTE WIE AUF DER KARTE. Ein Projekt geht
//    über SEINE Adresse (`/projekte/:id`): wählen, hinfliegen, Modal — das
//    kann die Karte schon, ein zweiter Weg dorthin wäre eine zweite Fassung
//    derselben Geste. Büro und Ort setzen ein Sprungziel (`useUi.sprung`),
//    das `Karte.tsx` verbraucht.
//
// 6. WER WÄHLT, KOMMT ZUR KARTE. Das Feld steht auf jeder Seite, auch auf
//    der Startseite; die Karte nur unter `/karte` und `/projekte/:id`
//    (lib/wege.ts). Ein Sprungziel, das auf `/team` gesetzt wird und liegen
//    bleibt, feuerte Minuten später beim nächsten Öffnen der Karte — als
//    Sprung ins Nirgendwo, den niemand mehr zuordnen kann. Darum wird
//    navigiert, sobald etwas gewählt ist, und das Ziel ist binnen einer
//    Sekunde verbraucht.
// ============================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStand } from "../../api/hooks";
import type { Buero, Ortstreffer, Person } from "../../api/types";
import { useEntprellt } from "../../hooks/useEntprellt";
import { ORT_ENTPRELLUNG, useOrtTreffer } from "../../hooks/useOrtTreffer";
import { LEERER_STAND, kartenstand, personTrifft, type Stift } from "../../screens/karte/hilfen";
import { useUi } from "../../store/ui";
import { useT } from "../../lib/texte";
import { KARTE, istKartenweg } from "../../lib/wege";
import { Avatar, Icon, Spinner } from "../ui";

/** Ab wann gesucht wird. Ein Buchstabe trifft fast alles und sagt nichts. */
const AB_ZEICHEN = 2;
/** Je Abschnitt. Mehr macht die Liste zu einer zweiten Ansicht. */
const JE_ABSCHNITT = 5;

type Zeile =
  | { art: "projekt"; id: string; titel: string; zusatz: string; stift: Stift }
  | { art: "person"; id: string; titel: string; zusatz: string; person: Person }
  | { art: "buero"; id: string; titel: string; zusatz: string; buero: Buero }
  | { art: "ort"; id: string; titel: string; zusatz: string; ort: Ortstreffer };

export function Suchliste({
  suche,
  offen,
  mobil = false,
  onSchliessen,
}: {
  suche: string;
  offen: boolean;
  /** Am Telefon: Enter heißt „fertig getippt", nicht „nimm die erste Zeile". */
  mobil?: boolean;
  onSchliessen: () => void;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: stand } = useStand();
  const setAuswahl = useUi((s) => s.setAuswahl);
  const setSprung = useUi((s) => s.setSprung);

  const entprellt = useEntprellt(suche.trim().toLowerCase(), ORT_ENTPRELLUNG);
  const [aktiv, setAktiv] = useState(0);
  const listeRef = useRef<HTMLDivElement>(null);

  // Bei jeder neuen Frage wieder oben anfangen — sonst zeigte der Marker auf
  // eine Zeile, die es in der neuen Liste gar nicht mehr gibt.
  useEffect(() => setAktiv(0), [entprellt]);

  const sucht = offen && entprellt.length >= AB_ZEICHEN;
  const { treffer: orte, laedt: ortLaedt } = useOrtTreffer(suche, sucht);

  // Dieselben Stifte wie die Karte — mit ihrem `suchtext`. Einmal je Stand
  // gerechnet (alle 60 s), nicht je Tastendruck.
  const { stifte } = useMemo(() => (stand ? kartenstand(stand) : LEERER_STAND), [stand]);

  const zeilen = useMemo<Zeile[]>(() => {
    if (!sucht || !stand) return [];
    const w = entprellt;
    const raus: Zeile[] = [];

    for (const s of stifte.filter((s) => s.suchtext.includes(w)).slice(0, JE_ABSCHNITT)) {
      raus.push({ art: "projekt", id: s.id, stift: s, titel: s.titel, zusatz: s.ort });
    }
    for (const p of stand.personen.filter((p) => personTrifft(p, w)).slice(0, JE_ABSCHNITT)) {
      raus.push({
        art: "person", id: p.id, person: p,
        titel: p.anzeigename,
        zusatz: p.titel,
      });
    }
    for (const b of stand.bueros.filter((b) => b.name.toLowerCase().includes(w)).slice(0, JE_ABSCHNITT)) {
      raus.push({ art: "buero", id: b.id, buero: b, titel: b.name, zusatz: b.ort });
    }
    for (const [i, o] of orte.slice(0, JE_ABSCHNITT).entries()) {
      raus.push({ art: "ort", id: `ort-${i}`, ort: o, titel: o.name, zusatz: o.zusatz ?? "" });
    }
    return raus;
  }, [sucht, stand, stifte, entprellt, orte]);

  // Wo die Karte steht, weiß `istKartenweg` (§32). Die eigene Fassung, die
  // hier bis dahin stand, nahm nur „board" aus — auf `/projekte/staende`
  // glaubte die Suche, sie stünde schon auf der Karte, navigierte nicht, und
  // der Sprung feuerte erst beim nächsten Kartenbesuch.
  const aufKarte = istKartenweg(pathname);
  const zurKarte = (): void => {
    if (!aufKarte) navigate(KARTE);
  };

  const waehlen = (z: Zeile): void => {
    if (z.art === "projekt") {
      // Über die Adresse: der `projektId`-Effekt der Karte wählt aus, fliegt
      // hin und öffnet das Modal — genau wie ein Klick auf den Stift.
      navigate(`/projekte/${z.id}`);
    } else if (z.art === "person") {
      setAuswahl({ art: "person", id: z.id });
      zurKarte();
    } else if (z.art === "buero") {
      setAuswahl({ art: "buero", id: z.id });
      // Die gespeicherte Ansicht des Bueros (033), wenn es eine hat — sonst
      // die Stadt drumherum (12: kein Mindestwert, ein Ziel). Wer auf einem
      // Grundstueck steht und „Hamburg" waehlt, will die Stadt sehen.
      const a = z.buero.ansicht;
      setSprung(a
        ? { punkt: z.buero.punkt, zoom: a.zoom, neigung: a.neigung, richtung: a.richtung }
        : { punkt: z.buero.punkt, zoom: 12 });
      zurKarte();
    } else {
      // Ein Ort ist keine Auswahl — er ist nur eine Stelle auf der Karte.
      setSprung({ punkt: z.ort.punkt, zoom: 15 });
      zurKarte();
    }
    onSchliessen();
  };

  // Die Tastatur hängt am FENSTER und nicht am Feld: das Feld gehört der
  // Kopfleiste, und ein zweiter Tastenhörer dort geriete mit ihrem eigenen
  // Escape ins Gehege. Der Hörer liest Zeilen und Marker aus einer Referenz —
  // so hängt er einmal, solange die Liste steht, statt nach jedem Anstrich
  // neu (und ohne veraltete Hülle).
  const lage = useRef({ zeilen, aktiv, waehlen });
  lage.current = { zeilen, aktiv, waehlen };
  const hatZeilen = zeilen.length > 0;
  useEffect(() => {
    if (!offen || !hatZeilen) return;
    const taste = (e: KeyboardEvent): void => {
      // Nur eine Liste, die man SIEHT, hört auf Tasten (Fehlerrunde 01.10.2026,
      // #16): eine zweite Instanz, die nur das CSS ausblendet (`display: none`
      // hat keine Rechtecke), wählte sonst mit Enter einen Treffer, den niemand sah.
      if ((listeRef.current?.getClientRects().length ?? 0) === 0) return;
      const { zeilen: z, aktiv: a, waehlen: w } = lage.current;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setAktiv((i) => (i + 1) % z.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setAktiv((i) => (i - 1 + z.length) % z.length);
      } else if (e.key === "Enter" && !mobil) {
        const zeile = z[a];
        if (zeile) {
          e.preventDefault();
          w(zeile);
        }
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [offen, hatZeilen, mobil]);

  // Die aktive Zeile in Sicht halten — mit der Tastatur läuft man sonst aus
  // dem sichtbaren Ausschnitt heraus und sieht nicht mehr, wo man ist.
  useEffect(() => {
    listeRef.current?.querySelector<HTMLElement>('[data-aktiv="ja"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [aktiv]);

  if (!offen || suche.trim().length < AB_ZEICHEN) return null;

  const laedt = ortLaedt && zeilen.length === 0;
  const ueberschrift: Record<Zeile["art"], string> = {
    projekt: t.nav.trefferProjekte,
    person: t.nav.trefferPersonen,
    buero: t.nav.trefferBueros,
    ort: t.nav.trefferOrte,
  };

  let letzte: Zeile["art"] | null = null;

  return (
    <div ref={listeRef} className="suchliste anim-pop" role="listbox" aria-label={t.nav.suchePlatzhalter}>
      {laedt && <span className="suchliste-leer"><Spinner /> {t.allgemein.laedt}</span>}
      {!laedt && zeilen.length === 0 && (
        <span className="suchliste-leer">{t.ortssuche.keineTreffer}</span>
      )}
      {zeilen.map((z, i) => {
        const kopf = z.art !== letzte ? ueberschrift[z.art] : null;
        letzte = z.art;
        return (
          <div key={`${z.art}-${z.id}`}>
            {kopf && <div className="suchliste-kopf">{kopf}</div>}
            <button
              type="button"
              role="option"
              aria-selected={i === aktiv}
              data-aktiv={i === aktiv ? "ja" : "nein"}
              className={`suchliste-zeile${i === aktiv ? " ist-an" : ""}`}
              onMouseEnter={() => setAktiv(i)}
              onClick={() => waehlen(z)}
            >
              {z.art === "person" ? (
                <Avatar person={z.person} size={22} />
              ) : (
                <Icon
                  size={15}
                  name={z.art === "projekt" ? "pin" : z.art === "buero" ? "building" : "map-pin"}
                />
              )}
              <span className="suchliste-name">{z.titel}</span>
              {z.zusatz && <span className="suchliste-zusatz">{z.zusatz}</span>}
            </button>
          </div>
        );
      })}
    </div>
  );
}
